import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function SriPage(): Node {
  return html`
    <div>
      <h1>SRI (Subresource Integrity)</h1>
      <p>SRI ensures that fetched remote modules haven't been tampered with. The browser (or onefold's loader) verifies a cryptographic hash of the file's content before execution.</p>

      <h2>How It Works</h2>
      <ol>
        <li>You compute a hash of the remote module at build/deploy time.</li>
        <li>You provide that hash in the <code>integrity</code> option.</li>
        <li>onefold fetches the module, computes its hash, and compares.</li>
        <li>If hashes don't match → load is rejected, <code>onError</code> fires.</li>
      </ol>

      <h2>Usage</h2>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widgets/billing.js',
  integrity: 'sha384-oqVuAfXRKap7fdgcCY5uykM6+R9GqQ8K/uxANQFe...',
  fallback: () => html\`<p>Loading...</p>\`,
  onError: (err) => html\`<p class="error">Integrity check failed</p>\`,
});`)}

      <h2>Generating Hashes</h2>
      <p>Use any of these commands to generate an SRI hash:</p>

      <h3>OpenSSL</h3>
      ${CodeBlock(`openssl dgst -sha384 -binary billing.js | openssl base64 -A`)}

      <h3>shasum + base64</h3>
      ${CodeBlock(`shasum -b -a 384 billing.js | awk '{ print $1 }' | xxd -r -p | base64`)}

      <h3>Node.js</h3>
      ${CodeBlock(`const crypto = require('crypto');
const fs = require('fs');

const content = fs.readFileSync('billing.js');
const hash = crypto.createHash('sha384').update(content).digest('base64');
console.log(\`sha384-\${hash}\`);`)}

      <h2>Attack Comparison</h2>
      <table>
        <tr><th>Attack</th><th>Without SRI</th><th>With SRI</th></tr>
        <tr><td>CDN compromise</td><td>Malicious code executes</td><td>Load rejected, fallback shown</td></tr>
        <tr><td>Man-in-the-middle</td><td>Injected code runs</td><td>Hash mismatch, blocked</td></tr>
        <tr><td>DNS hijacking</td><td>Fake module loads</td><td>Origin check + hash fails</td></tr>
        <tr><td>Cache poisoning</td><td>Stale/malicious cache served</td><td>Hash mismatch, blocked</td></tr>
      </table>

      ${Callout('SRI hashes must be regenerated every time the remote module is rebuilt. Automate this in your CI/CD pipeline.', 'warn')}

      <h2>Hash Validity</h2>
      <p>Supported algorithms (in order of preference):</p>
      <ul>
        <li><strong>sha512</strong> — strongest, recommended for high-security environments</li>
        <li><strong>sha384</strong> — standard, used by most CDNs</li>
        <li><strong>sha256</strong> — minimum acceptable strength</li>
      </ul>
      <p>The integrity string format is: <code>algorithm-base64hash</code></p>
      ${CodeBlock(`// Valid formats
'sha384-oqVuAfXRKap7fdgcCY5uykM6+R9GqQ8K/ux...'
'sha512-abc123def456...'
'sha256-xyz789...'`)}
    </div>
  `;
}
